import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BookMarked, BookOpen, Globe, Users, ShieldCheck, EyeOff, Lock } from "lucide-react";

const ICONS = { everyone: Globe, users: Users, admins: ShieldCheck, hidden: EyeOff };
const HINTS = {
  everyone: "Visible to every visitor, signed in or not.",
  users: "Only signed-in members can open it; visitors are asked to sign in.",
  admins: "Only site admins can see it — handy while reviewing a new import.",
  hidden: "Removed everywhere for everyone, admins included.",
};

/* One card per resource with the four access radios. `items` need {key, title, meta, access, locked?, lockNote?}. */
export function AccessCards({ idPrefix, levels, items, onChange, Icon }) {
  return (
    <div className="space-y-4">
      {items.map((it) => (
        <div key={it.key} className="rounded-2xl border border-border p-4 sm:p-5" data-testid={`${idPrefix}-${it.key}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-gold" />
                <h3 className="font-serif text-xl font-semibold text-foreground">{it.title}</h3>
              </div>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">{it.meta}</p>
              {it.description && <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{it.description}</p>}
            </div>
            <span className="rounded-full border border-gold/40 bg-gold/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-gold" data-testid={`${idPrefix}-${it.key}-current`}>
              {it.locked ? "Always on" : levels.find((l) => l.key === it.access)?.label}
            </span>
          </div>
          {it.locked ? (
            <p className="mt-4 inline-flex items-center gap-2 rounded-xl border border-dashed border-border px-3 py-2 text-sm text-muted-foreground" data-testid={`${idPrefix}-${it.key}-locked`}><Lock className="h-4 w-4 text-gold" /> {it.lockNote}</p>
          ) : (
            <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4" role="radiogroup" aria-label={`Access for ${it.title}`}>
              {levels.map((l) => {
                const LevelIcon = ICONS[l.key];
                const on = it.access === l.key;
                return (
                  <button key={l.key} type="button" role="radio" aria-checked={on} onClick={() => !on && onChange(it.key, l.key)} data-testid={`${idPrefix}-${it.key}-${l.key}`}
                    className={`flex items-start gap-2.5 rounded-xl border p-3 text-left transition-colors ${on ? "border-gold bg-gold/10" : "border-border hover:border-gold/60"}`}>
                    <LevelIcon className={`mt-0.5 h-4 w-4 shrink-0 ${on ? "text-gold" : "text-muted-foreground"}`} />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-foreground">{l.label}</span>
                      <span className="block text-xs leading-snug text-muted-foreground">{HINTS[l.key]}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function AdminCommentaries() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-commentaries"], queryFn: async () => (await api.get("/admin/commentaries")).data });
  const { data: bibles } = useQuery({ queryKey: ["admin-bible-versions"], queryFn: async () => (await api.get("/admin/bible-versions")).data });
  if (!data || !bibles) return <div className="h-40 animate-pulse rounded-2xl bg-muted" />;

  const setCommentary = async (slug, access) => {
    try {
      await api.put(`/admin/commentaries/${slug}/access`, { access });
      qc.setQueryData(["admin-commentaries"], (d) => ({ ...d, commentaries: d.commentaries.map((c) => (c.slug === slug ? { ...c, access } : c)) }));
      qc.invalidateQueries({ queryKey: ["commentaries"] });
      toast.success(`${data.commentaries.find((c) => c.slug === slug)?.name}: ${data.levels.find((l) => l.key === access)?.label}.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setBible = async (code, access) => {
    try {
      await api.put(`/admin/bible-versions/${code}/access`, { access });
      qc.setQueryData(["admin-bible-versions"], (d) => ({ ...d, versions: d.versions.map((v) => (v.code === code ? { ...v, access } : v)) }));
      qc.invalidateQueries({ queryKey: ["bible-books"] });
      toast.success(`${code}: ${bibles.levels.find((l) => l.key === access)?.label}.`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-commentaries">
      <section className="rounded-2xl border border-border bg-card p-5">
        <h2 className="font-serif text-2xl font-semibold text-foreground">Who can read each commentary</h2>
        <p className="mt-1 text-sm text-muted-foreground">Changes apply immediately across the site: the Commentary pages, the Bible Reader panel, question pages and journal @-mentions.</p>
        <div className="mt-6">
          <AccessCards idPrefix="admin-commentary" levels={data.levels} onChange={setCommentary} Icon={BookMarked}
            items={data.commentaries.map((c) => ({ key: c.slug, title: c.full_name, meta: `${c.author} · ${c.year} · ${c.chapters} chapters · ${c.sections.toLocaleString()} sections`, access: c.access }))} />
        </div>
      </section>
      <section className="rounded-2xl border border-border bg-card p-5" data-testid="admin-bible-versions">
        <h2 className="font-serif text-2xl font-semibold text-foreground">Who can read each Bible version</h2>
        <p className="mt-1 text-sm text-muted-foreground">Controls the version toggle in the Bible Reader (standalone and beside questions). The KJV is the base text and always stays on.</p>
        <div className="mt-6">
          <AccessCards idPrefix="admin-bible" levels={bibles.levels} onChange={setBible} Icon={BookOpen}
            items={bibles.versions.map((v) => ({ key: v.code, title: `${v.name} (${v.code})`, meta: `${v.year}`, description: v.description, access: v.access, locked: v.locked, lockNote: "Always available — word studies, journal mentions and search are built on the KJV." }))} />
        </div>
      </section>
    </div>
  );
}
